import { BaseService, PagedResult, QueryParams } from './base/base.service';

/**
 * Reward (Prize Draw) DTO
 */
export interface Reward {
  id: string;
  title: string;
  description: string;
  shortDescription: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  galleryImages: string[];
  tokensRequired: number;
  drawType: DrawType;
  drawMode: DrawMode;
  maxParticipants: number | null;
  winnerCount: number;
  currentParticipants: number;
  startDate: string;
  endDate: string;
  drawExecutedAt: string | null;
  status: RewardStatus;
  category: string | null;
  tags: string[];
  rules: string | null;
  isPublic: boolean;
  isFeatured: boolean;
  creationTime: string;
}

/**
 * Draw Type Enum
 */
export enum DrawType {
  Automatic = 1,
  Manual = 2,
}

/**
 * Draw Mode Enum
 */
export enum DrawMode {
  Random = 1,
  Lottery = 2,
}

/**
 * Reward Status Enum
 */
export enum RewardStatus {
  Draft = 1,
  Active = 2,
  Closed = 3,
  Drawn = 4,
  Cancelled = 5,
}

/**
 * Create/Update Reward Request
 */
export interface RewardRequest {
  title: string;
  description: string;
  shortDescription?: string | null;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  galleryImages?: string[];
  tokensRequired: number;
  drawType: DrawType;
  drawMode: DrawMode;
  maxParticipants?: number | null;
  winnerCount: number;
  startDate: string;
  endDate: string;
  status?: RewardStatus;
  category?: string | null;
  tags?: string[];
  rules?: string | null;
  isPublic: boolean;
  isFeatured: boolean;
}

/**
 * Reward Participation DTO
 */
export interface RewardParticipation {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string | null;
  rewardId: string;
  tokensBid: number;
  lotteryNumber: number | null;
  participationDate: string;
  isWinner: boolean;
}

/**
 * Reward Winner DTO
 */
export interface RewardWinner {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string | null;
  rewardId: string;
  rewardTitle: string;
  winningRank: number;
  tokensBid: number;
  lotteryNumber: number | null;
  wonAt: string;
  claimStatus: WinnerClaimStatus;
  claimedAt: string | null;
}

/**
 * Winner Claim Status Enum
 */
export enum WinnerClaimStatus {
  Unclaimed = 0,
  Claimed = 1,
  Expired = 2,
}

/**
 * Execute Draw Request
 */
export interface ExecuteDrawRequest {
  selectedUserIds?: string[];
}

/**
 * Execute Draw Response
 */
export interface ExecuteDrawResponse {
  rewardId: string;
  drawExecutedAt: string;
  drawType: DrawType;
  winners: RewardWinner[];
  totalParticipants: number;
  notificationsSent: boolean;
}

/**
 * Reward Query Parameters
 */
export interface RewardQueryParams extends QueryParams {
  status?: RewardStatus;
  isFeatured?: boolean;
  category?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Rewards Service
 * Provides rewards (prize draws) management functionality for admin panel
 */
export class RewardsService extends BaseService {
  constructor() {
    super('/api/engagement');
  }

  /**
   * Get all rewards
   */
  async getRewards(params: RewardQueryParams = {}): Promise<PagedResult<Reward>> {
    const queryParams = new URLSearchParams();
    if (params.status !== undefined) queryParams.append('status', params.status.toString());
    if (params.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured.toString());
    if (params.category) queryParams.append('category', params.category);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.skipCount !== undefined) queryParams.append('skipCount', params.skipCount.toString());
    if (params.maxResultCount !== undefined) queryParams.append('maxResultCount', params.maxResultCount.toString());
    
    const response = await this.api.get<PagedResult<Reward>>(`/admin/rewards?${queryParams}`);
    return response.data;
  }

  /**
   * Get reward by ID
   */
  async getReward(id: string): Promise<Reward> {
    const response = await this.api.get<Reward>(`/admin/rewards/${id}`);
    return response.data;
  }

  /**
   * Create reward
   */
  async createReward(request: RewardRequest): Promise<Reward> {
    const response = await this.api.post<Reward>('/admin/rewards', request);
    return response.data;
  }

  /**
   * Update reward
   */
  async updateReward(id: string, request: RewardRequest): Promise<Reward> {
    const response = await this.api.put<Reward>(`/admin/rewards/${id}`, request);
    return response.data;
  }

  /**
   * Delete reward
   */
  async deleteReward(id: string): Promise<void> {
    await this.api.delete(`/admin/rewards/${id}`);
  }

  /**
   * Close reward
   */
  async closeReward(id: string): Promise<Reward> {
    const response = await this.api.post<Reward>(`/admin/rewards/${id}/close`);
    return response.data;
  }

  /**
   * Execute draw
   */
  async executeDraw(id: string, request?: ExecuteDrawRequest): Promise<ExecuteDrawResponse> {
    const response = await this.api.post<ExecuteDrawResponse>(`/admin/rewards/${id}/execute-draw`, request || {});
    return response.data;
  }

  /**
   * Get reward participations
   */
  async getParticipations(rewardId: string, params: QueryParams = {}): Promise<PagedResult<RewardParticipation>> {
    const queryParams = new URLSearchParams();
    if (params.filter) queryParams.append('filter', params.filter);
    if (params.skipCount !== undefined) queryParams.append('skipCount', params.skipCount.toString());
    if (params.maxResultCount !== undefined) queryParams.append('maxResultCount', params.maxResultCount.toString());
    
    const response = await this.api.get<PagedResult<RewardParticipation>>(`/admin/rewards/${rewardId}/participations?${queryParams}`);
    return response.data;
  }

  /**
   * Get reward winners
   */
  async getWinners(rewardId: string): Promise<RewardWinner[]> {
    const response = await this.api.get<RewardWinner[]>(`/admin/rewards/${rewardId}/winners`);
    return response.data;
  }

  /**
   * Mark winner as claimed
   */
  async markWinnerClaimed(winnerId: string): Promise<void> {
    await this.api.post(`/admin/rewards/winners/${winnerId}/claim`);
  }

  /**
   * Mark winner as unclaimed
   */
  async markWinnerUnclaimed(winnerId: string): Promise<void> {
    await this.api.post(`/admin/rewards/winners/${winnerId}/unclaim`);
  }
}

// Export singleton instance
export const rewardsService = new RewardsService();
